import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Instagram, ArrowUpRight } from "lucide-react";

const HANDLE = "barique_couture";
const PROFILE_URL = `https://www.instagram.com/${HANDLE}/`;
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const proxyImage = (raw) => {
  if (!raw) return "";
  if (raw.includes("cdninstagram.com") || raw.includes("fbcdn.net")) return `${BACKEND}/api/instagram/image?url=${encodeURIComponent(raw)}`;
  return raw;
};

function InstagramProfileEmbed() {
  useEffect(() => {
    const container = document.getElementById("barique-instagram-profile-embed");
    if (!container) return;
    container.innerHTML = "";
    const quote = document.createElement("blockquote");
    quote.className = "instagram-media";
    quote.setAttribute("data-instgrm-permalink", PROFILE_URL);
    quote.setAttribute("data-instgrm-version", "14");
    quote.style.cssText = "background:#fff;border:0;border-radius:0;box-shadow:none;margin:0 auto;max-width:540px;min-width:326px;padding:0;width:calc(100% - 24px);";
    container.appendChild(quote);

    const existing = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
    if (existing) {
      window.instgrm?.Embeds?.process?.();
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.instagram.com/embed.js";
    script.onload = () => window.instgrm?.Embeds?.process?.();
    document.body.appendChild(script);
  }, []);

  return <div id="barique-instagram-profile-embed" className="mt-10 flex justify-center overflow-hidden" data-testid="instagram-profile-embed" />;
}

export default function InstagramFeed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiFailed, setApiFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/instagram/feed?handle=${HANDLE}&limit=6`);
        const next = Array.isArray(data?.posts) ? data.posts.filter(p => p?.image) : [];
        if (!cancelled) {
          setPosts(next);
          setApiFailed(next.length === 0);
        }
      } catch {
        if (!cancelled) {
          setPosts([]);
          setApiFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="py-24 bg-ivory" data-testid="instagram-feed">
      <div className="container-lux text-center">
        <p className="overline text-muted-foreground">Follow The Journal</p>
        <h2 className="mt-4 font-serif tracking-tighter text-4xl md:text-5xl font-light">@{HANDLE}</h2>
        <a href={PROFILE_URL} target="_blank" rel="noreferrer" data-testid="ig-profile-link" className="mt-6 inline-flex items-center gap-2 overline hover:opacity-70 transition-opacity">
          <Instagram size={14} strokeWidth={1.25} /><span>Follow on Instagram</span><ArrowUpRight size={14} strokeWidth={1.25} />
        </a>
      </div>

      {loading ? (
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="relative aspect-square overflow-hidden bg-muted animate-pulse" data-testid={`ig-skeleton-${i}`} />
          ))}
        </div>
      ) : posts.length > 0 ? (
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1">
          {posts.slice(0, 6).map((p, i) => (
            <a key={p.shortcode || i} href={p.permalink || PROFILE_URL} target="_blank" rel="noreferrer" data-testid={`ig-${i}`} className="relative aspect-square overflow-hidden group bg-muted">
              <img src={proxyImage(p.image)} loading="lazy" referrerPolicy="no-referrer" alt={`@${HANDLE} post ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors duration-500 flex items-center justify-center"><Instagram size={22} strokeWidth={1} className="text-ivory opacity-0 group-hover:opacity-100 transition-opacity duration-300" /></div>
              {p.is_video && <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest px-2 py-0.5 bg-ink/70 text-ivory">Reel</span>}
            </a>
          ))}
        </div>
      ) : (
        <InstagramProfileEmbed />
      )}

      {apiFailed && !loading && (
        <p className="mt-5 text-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Live Instagram content · <a href={PROFILE_URL} target="_blank" rel="noreferrer" className="underline">View @barique_couture</a>
        </p>
      )}
    </section>
  );
}
